import React from 'react';
import { Link } from 'react-router-dom';
import { Card, Badge, Button } from 'flowbite-react';
import { HiOfficeBuilding, HiAcademicCap, HiPlus, HiX, HiExclamation } from 'react-icons/hi';
import type { PersonOrganizationRole } from '../../types/api';

// Helper to format date strings without timezone offset issues
const formatDateLocal = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString();
};

interface OrganizationData {
  educationOrganizationId: number;
  organizationName: string;
  organizationType: 'lea' | 'school';
  leaId?: number | null;  // LocalEducationAgency.leaId - used for navigation (/leas/:leaId)
  parentLeaEducationOrganizationId?: number | null;  // Parent LEA's educationOrganizationId - for permission checking
  schoolId?: number | null;
}

interface OrganizationAssignmentCardProps {
  organization: OrganizationData;
  primaryAssignment: PersonOrganizationRole | null;  // includeInDirectory=true, may be null for JIT users
  roleTags: PersonOrganizationRole[];  // includeInDirectory=false
  // For a school: tags at its district when the person has no district
  // position. The Role filter counts these for the school position.
  inheritedRoleTags?: PersonOrganizationRole[];
  canEdit: boolean;
  onAddRole?: () => void;
  onEditRole?: (assignmentId: number) => void;
  onDeleteRole?: (assignmentId: number) => void;
}

/** Check if an assignment's end date is in the past */
const isAssignmentPast = (role: PersonOrganizationRole): boolean => {
  if (!role.endDate) return false;
  return new Date(role.endDate) < new Date();
};

export const OrganizationAssignmentCard: React.FC<OrganizationAssignmentCardProps> = ({
  organization,
  primaryAssignment,
  roleTags,
  inheritedRoleTags = [],
  canEdit,
  onAddRole,
  onEditRole: _onEditRole,
  onDeleteRole
}) => {
  // Only link to org profile if we have a valid leaId (schools don't have their own profile page)
  const canLink = organization.leaId != null;
  const orgLink = `/leas/${organization.leaId}`;

  const OrgIcon = organization.organizationType === 'lea' ? HiOfficeBuilding : HiAcademicCap;

  // Determine if the primary assignment (or all visible roles) are past
  const isPrimaryPast = primaryAssignment ? isAssignmentPast(primaryAssignment) : false;
  const allPast = (!primaryAssignment || isPrimaryPast) && roleTags.every(isAssignmentPast);

  // A current directory entry with no role (on the entry or as a tag) can't
  // be found with the Role filter in Search Contacts
  const notFindableByRole = !!primaryAssignment && !isPrimaryPast &&
    !!primaryAssignment.positionTitle?.trim() &&
    !primaryAssignment.roleDescriptorId &&
    ![...roleTags, ...inheritedRoleTags].some(tag => !isAssignmentPast(tag));

  return (
    <Card className={`p-5 ${allPast ? 'opacity-60' : ''}`}>
      <div className="flex justify-between items-start">
        <div className="flex-1">
          {/* Organization header with link */}
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center flex-shrink-0">
              <OrgIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              {canLink ? (
                <Link
                  to={orgLink}
                  className="text-lg font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline"
                >
                  {organization.organizationName}
                </Link>
              ) : (
                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                  {organization.organizationName}
                </span>
              )}

              {/* Position title */}
              {primaryAssignment?.positionTitle && (
                <p className="text-gray-700 dark:text-gray-300 mt-1">
                  {primaryAssignment.positionTitle}
                </p>
              )}

              {/* Date range for primary assignment */}
              {primaryAssignment && (primaryAssignment.beginDate || primaryAssignment.endDate) && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {primaryAssignment.beginDate && formatDateLocal(primaryAssignment.beginDate)}
                  {primaryAssignment.beginDate && ' – '}
                  {primaryAssignment.endDate ? formatDateLocal(primaryAssignment.endDate) : 'Present'}
                </p>
              )}

              {/* Role tags section */}
              {roleTags.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Roles</p>
                  <div className="flex flex-wrap gap-2">
                    {roleTags.map((tag) => (
                      <div key={tag.assignmentId} className="inline-flex items-center gap-1">
                        <Badge color={isAssignmentPast(tag) ? 'gray' : 'info'} size="sm">
                          {tag.roleName || tag.positionTitle || 'Custom Role'}
                        </Badge>
                        {canEdit && onDeleteRole && (
                          <button
                            onClick={() => onDeleteRole(tag.assignmentId)}
                            className="p-0.5 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/50 rounded transition-colors"
                            aria-label={`Remove ${tag.roleName || 'role'}`}
                          >
                            <HiX className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {notFindableByRole && canEdit && (
                <p className="mt-3 inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400">
                  <HiExclamation className="h-3.5 w-3.5" />
                  No role tags yet, so the Role filter in Search Contacts can't find this contact.
                </p>
              )}

              {/* Add role button */}
              {canEdit && onAddRole && (
                <Button
                  size="xs"
                  color="light"
                  onClick={onAddRole}
                  className="mt-3 text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100"
                >
                  <HiPlus className="mr-1 h-3 w-3" />
                  Add Role Tag
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Status badges */}
        <div className="flex flex-col items-end gap-1">
          {primaryAssignment?.isPrimary && (
            <Badge color="success" size="sm">
              Primary
            </Badge>
          )}
          {allPast && (
            <Badge color="gray" size="sm">
              Past
            </Badge>
          )}
        </div>
      </div>
    </Card>
  );
};